import React, { useState, useEffect } from 'react';
import { accountingApi } from '../../../services/accountingApi';

const fmt = (n) => `$ ${ (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }) }`;

const COLORS = ['#E75E82', '#37A2EC', '#FACD56', '#5CA85C', '#605CA8', '#2f7ed8', '#0d233a', '#8bbc21', '#910000', '#1aadce'];

const DATE_RANGES = [
  { label: 'Today', key: 'today' },
  { label: 'Yesterday', key: 'yesterday' },
  { label: 'Last 7 Days', key: '7_days' },
  { label: 'Last 30 Days', key: '30_days' },
  { label: 'This Month', key: 'this_month' },
  { label: 'Last Month', key: 'last_month' },
  { label: 'This month last year', key: 'this_month_last_year' },
  { label: 'This Year', key: 'this_year' },
  { label: 'Last Year', key: 'last_year' },
  { label: 'Current financial year', key: 'current_fy' },
  { label: 'Last financial year', key: 'last_fy' },
];

const getDateRangeParams = (key) => {
  const now = new Date();
  const formatYMD = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  if (key === 'today') {
    return { start_date: formatYMD(now), end_date: formatYMD(now) };
  }
  if (key === 'yesterday') {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    return { start_date: formatYMD(y), end_date: formatYMD(y) };
  }
  if (key === '7_days') {
    const s = new Date(now);
    s.setDate(s.getDate() - 6);
    return { start_date: formatYMD(s), end_date: formatYMD(now) };
  }
  if (key === '30_days') {
    const s = new Date(now);
    s.setDate(s.getDate() - 29);
    return { start_date: formatYMD(s), end_date: formatYMD(now) };
  }
  if (key === 'this_month') {
    const s = new Date(now.getFullYear(), now.getMonth(), 1);
    const e = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start_date: formatYMD(s), end_date: formatYMD(e) };
  }
  if (key === 'last_month') {
    const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const e = new Date(now.getFullYear(), now.getMonth(), 0);
    return { start_date: formatYMD(s), end_date: formatYMD(e) };
  }
  if (key === 'this_month_last_year') {
    const s = new Date(now.getFullYear() - 1, now.getMonth(), 1);
    const e = new Date(now.getFullYear() - 1, now.getMonth() + 1, 0);
    return { start_date: formatYMD(s), end_date: formatYMD(e) };
  }
  if (key === 'this_year') {
    const s = new Date(now.getFullYear(), 0, 1);
    const e = new Date(now.getFullYear(), 11, 31);
    return { start_date: formatYMD(s), end_date: formatYMD(e) };
  }
  if (key === 'last_year') {
    const s = new Date(now.getFullYear() - 1, 0, 1);
    const e = new Date(now.getFullYear() - 1, 11, 31);
    return { start_date: formatYMD(s), end_date: formatYMD(e) };
  }
  return {};
};

/* Custom SVG Donut Pie Chart Component matching Highcharts */
const DonutPieChart = ({ data, size = 220 }) => {
  const items = data || [];
  const total = items.reduce((acc, item) => acc + (parseFloat(item.balance || item.raw_balance) || 0), 0);
  let accumulatedAngle = 0;

  // Equal division or proportional
  const isZero = total === 0;

  const slices = items.map((item, index) => {
    const val = parseFloat(item.balance || item.raw_balance) || 0;
    const angle = isZero ? (360 / Math.max(1, items.length)) : (val / total) * 360;
    const startAngle = accumulatedAngle;
    accumulatedAngle += angle;

    const x1 = Math.cos((Math.PI * (startAngle - 90)) / 180);
    const y1 = Math.sin((Math.PI * (startAngle - 90)) / 180);
    const x2 = Math.cos((Math.PI * (startAngle + angle - 90)) / 180);
    const y2 = Math.sin((Math.PI * (startAngle + angle - 90)) / 180);

    const largeArcFlag = angle > 180 ? 1 : 0;
    const pathData = `M 0 0 L ${x1} ${y1} A 1 1 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

    return {
      pathData,
      color: isZero ? '#e2e8f0' : COLORS[index % COLORS.length],
      label: item.label || item.name,
      balance: val,
    };
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', width: '100%' }}>
      <div style={{ position: 'relative', width: `${size}px`, height: `${size}px` }}>
        {isZero ? (
          /* Empty circle outline matching Highcharts zero state */
          <svg viewBox="-1.1 -1.1 2.2 2.2" style={{ width: '100%', height: '100%' }}>
            <circle cx="0" cy="0" r="0.95" fill="none" stroke="#e2e8f0" strokeWidth="0.12" />
          </svg>
        ) : (
          <svg viewBox="-1.2 -1.2 2.4 2.4" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
            {slices.map((slice, i) => (
              <path key={i} d={slice.pathData} fill={slice.color} stroke="#fff" strokeWidth="0.02" />
            ))}
          </svg>
        )}
      </div>

      {/* Legend Badges */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.85rem', justifyContent: 'center' }}>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>
            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: COLORS[i % COLORS.length] }}></span>
            <span>{item.label || item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AccountingDashboard = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [selectedRange, setSelectedRange] = useState('current_fy');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  const fetchDashboard = () => {
    setLoading(true);
    const params = getDateRangeParams(selectedRange);
    accountingApi.getDashboard(params)
      .then(res => setData(res))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDashboard();
  }, [selectedRange]);

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>Loading Accounting Overview...</div>;
  if (error)   return <div style={{ padding: '3rem', textAlign: 'center', color: '#ef4444' }}>Error: {error}</div>;

  const overview = data?.overview || [];
  const subCharts = data?.sub_charts || {};

  const currentSelectedLabel = DATE_RANGES.find(r => r.key === selectedRange)?.label || 'Filter by date';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* Date Filter Bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', position: 'relative' }}>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowFilterDropdown(p => !p)}
            style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.55rem 1.25rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 4px 6px -1px rgba(79,70,229,0.3)' }}
          >
            📊 {currentSelectedLabel} ▾
          </button>
          {showFilterDropdown && (
            <div style={{ position: 'absolute', right: 0, top: '2.6rem', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.15)', zIndex: 100, minWidth: '210px', overflow: 'hidden' }}>
              {DATE_RANGES.map(r => (
                <div
                  key={r.key}
                  onClick={() => { setSelectedRange(r.key); setShowFilterDropdown(false); }}
                  style={{
                    padding: '0.55rem 1rem', fontSize: '0.85rem', cursor: 'pointer', fontWeight: selectedRange === r.key ? 700 : 500,
                    background: selectedRange === r.key ? '#0ea5e9' : 'transparent',
                    color: selectedRange === r.key ? '#fff' : '#1e293b',
                  }}
                >
                  {r.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chart of accounts overview Card */}
      <div style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
        <h3 style={{ margin: '0 0 1.25rem 0', fontSize: '1.15rem', fontWeight: 800, color: '#1e293b' }}>
          Chart of accounts overview
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', alignItems: 'center' }}>
          {/* Left Table */}
          <div style={{ overflowX: 'auto' }}>
            <table className="acc-table" style={{ width: '100%' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ textAlign: 'left' }}>Account Type</th>
                  <th style={{ textAlign: 'left' }}>Current Balance</th>
                </tr>
              </thead>
              <tbody>
                {overview.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, color: '#334155' }}>{row.label}</td>
                    <td style={{ fontWeight: 700, color: '#0f172a' }}>{fmt(row.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Right Pie Chart */}
          <div>
            <DonutPieChart data={overview} size={220} />
          </div>
        </div>
      </div>

      {/* Subtype Charts Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
        {Object.keys(subCharts).map((catKey) => {
          const items = subCharts[catKey] || [];
          const catTitle = catKey.charAt(0).toUpperCase() + catKey.slice(1).toLowerCase();

          return (
            <div key={catKey} style={{ background: '#fff', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>{catTitle}</h4>
                <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>☰</span>
              </div>
              <div style={{ minHeight: '220px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
                <DonutPieChart data={items} size={160} />
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default AccountingDashboard;
